import React, { useState, useEffect } from 'react';
import { sources as sourcesApi } from '../../api/client';
import { Globe, Loader2 } from 'lucide-react';

export default function SourcesPage({ activeBrandId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await sourcesApi.get(activeBrandId);
        setData(result.sources || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadData();
  }, [activeBrandId]);

  const typeColors = {
    REDDIT: 'bg-orange-500', BLOG: 'bg-blue-500', OFFICIAL: 'bg-emerald-500',
    REVIEW_SITE: 'bg-violet-500', NEWS: 'bg-amber-500', FORUM: 'bg-cyan-500',
    WIKIPEDIA: 'bg-stone-500', SOCIAL: 'bg-pink-500', OTHER: 'bg-stone-400',
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Analytics</span>
        <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Citation Sources</h1>
        <p className="text-stone-500 mt-1">Where LLMs find their information about your brand</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <>
          {/* Visual Breakdown */}
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-6">Source Distribution</h2>
            <div className="flex h-8 overflow-hidden mb-4">
              {data.map((source, i) => (
                <div key={i} className={`${typeColors[source.type] || 'bg-stone-400'} transition-all`}
                  style={{ width: `${source.percentage}%` }} title={`${source.type}: ${source.percentage}%`} />
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              {data.map((source, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-3 h-3 ${typeColors[source.type] || 'bg-stone-400'}`} />
                  <span className="text-sm text-stone-600 dark:text-stone-300">{source.type}</span>
                  <span className="text-sm text-stone-400">{source.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed List */}
          <div className="space-y-3">
            {data.map((source, i) => (
              <div key={i} className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded flex items-center justify-center ${typeColors[source.type] || 'bg-stone-400'}`}>
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900 dark:text-white">{source.type}</p>
                      <p className="text-sm text-stone-500">{source.count} citations</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-2xl text-stone-900 dark:text-white">{source.percentage}%</div>
                    <div className="text-xs text-stone-400">of total</div>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-stone-100 dark:bg-stone-700 overflow-hidden">
                  <div className={`h-full ${typeColors[source.type] || 'bg-stone-400'}`} style={{ width: `${source.percentage}%` }} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
